import React, { useState, useEffect } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import {
  makeStyles,
  Typography,
  Card,
  Box,
  Container,
  Link,
  Button
} from '@material-ui/core';

import { t } from 'src/shared/literals';
import Page from 'src/shared/ui/components/Page';
import Form from 'src/shared/ui/components/Form';
import processFormErrorMessages from 'src/shared/ui/components/Form/FormErrorMessage';

import validateSchema from 'src/shared/models/SchemaValidator';
import CustomerContact from 'src/customer-contact/models/CustomerContact';

import createCustomerContact from 'src/customer-contact/use-cases/createCustomerContact';
import getCustomerContactById from 'src/customer-contact/use-cases/getCustomerContactById';
import updateCustomerContactById from 'src/customer-contact/use-cases/updateCustomerContactById';

import getContractTypeList from 'src/contract-type/use-cases/getContractTypeList';

import getPaymentTypeList from 'src/payment-type/use-cases/getPaymentTypeList';

import getCustomerContactList from 'src/customer-contact/use-cases/getCustomerContactList';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  saveButton: {
    top: '-20px'
  },
  backButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  searchButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  scrollBox: {
    overflow: 'scroll',
    height: '80vh'
  }
}));

const CustomerContactForm = ({
  customerContactId,
  customerId,
  cancel,
  success
}) => {
  const companyHeadquarterId = localStorage.selectedUserHeadquarter;
  const classes = useStyles();

  const [formFields, setFormFields] = useState([]);
  const [isRequesting, setIsRequesting] = useState(false);

  const initFormFields = async (fields) => {
    const contractTypeList = await getContractTypeList({
      companyHeadquarterId,
      page: null,
      limit: null,
      orderBy: ''
    });

    const paymentTypeList = await getPaymentTypeList({
      page: null,
      limit: null,
      orderBy: ''
    });

    const instance = CustomerContact({
      fields,
      contractTypeList: contractTypeList.rows,
      paymentTypeList
    });

    setFormFields(instance);
  };

  const fetchForm = async () => {
    const record = await getCustomerContactById(customerContactId);

    initFormFields(record);
  };

  useEffect(() => {
    if (customerContactId === 'new') {
      initFormFields();
    } else {
      fetchForm();
    }
  }, []);

  const validateFormFields = async (formFieldsValues) => {
    const validatedFormFields = await validateSchema(formFieldsValues);
    formFieldsValues = processFormErrorMessages(
      validatedFormFields,
      formFieldsValues
    );
    setFormFields(formFieldsValues.fields);

    return {
      errors: formFieldsValues.errors,
      fields: validatedFormFields
    };
  };

  const handleChangeFormFieldsValues = async (formFieldsValues, target) => {
    setFormFields(formFieldsValues);
  };

  const searchContact = async () => {
    const contactDocument = formFields && formFields[0].value;

    if (!contactDocument || contactDocument.length < 5) {
      alert('El documento debe tener al menos 6 caracteres').return;
    }

    const customerContactList = await getCustomerContactList({
      headquarterCustomerId: null,
      search: contactDocument,
      orderBy: '',
      page: null,
      limit: null
    });

    if (customerContactList && customerContactList.count) {
      let fields = customerContactList.rows[0];

      try {
        delete fields.id;
      } catch (e) {
        console.log(e);
      }

      try {
        delete fields.headquarterCustomerId;
      } catch (e) {
        console.log(e);
      }

      initFormFields(fields);
    } else {
      alert('No se encontró ningún contacto con el documento facilitado');
    }
  };

  const postForm = async () => {
    if (isRequesting) {
      return;
    }

    const validatedForm = await validateFormFields(formFields);
    if (validatedForm.errors) {
      alert('Rellene todos los campos correctamente.');
      return;
    }

    setIsRequesting(true);
    let result;
    if (customerContactId === 'new') {
      validatedForm.fields.headquarterCustomerId = customerId;

      result = await createCustomerContact(validatedForm.fields);
    } else {
      result = await updateCustomerContactById(
        validatedForm.fields,
        customerContactId
      );
    }

    setIsRequesting(false);

    if (typeof result.id === 'undefined') {
      if (t(result.error)) {
        alert(t(result.error));
      } else {
        alert('Se ha producido un error, vuelva a intentarlo');
      }
    } else {
      success();
    }
  };

  return (
    <Page className={classes.root} title="Cliente">
      <Container maxWidth={false}>
        <Box className={classes.navigation} display="flex">
          <Typography color="textSecondary" variant="body2">
            <Link component={RouterLink} to="" onClick={cancel}>
              Contactos
            </Link>
            {` >  ${
              formFields.length && formFields[0].value.length
                ? formFields[0].value
                : 'Nuevo'
            }`}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <Button className={classes.backButton} onClick={cancel}>
            {'Volver'}
          </Button>

          <Button className={classes.searchButton} onClick={searchContact}>
            Buscar contacto
          </Button>

          <Button
            color="primary"
            variant="contained"
            className={classes.saveButton}
            onClick={postForm}
          >
            Guardar contacto
          </Button>
        </Box>
        <Card>
          <Form
            fields={formFields}
            onChangeFields={handleChangeFormFieldsValues}
          />
        </Card>
      </Container>
    </Page>
  );
};

export default CustomerContactForm;
